import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from '@/context/AuthContext';
import HomePage from '@/pages/HomePage';
import AboutUsPage from '@/pages/AboutUsPage';
import ITRServicesPage from '@/pages/ITRServicesPage';
import GSTServicesPage from '@/pages/GSTServicesPage';
import ContactUsPage from '@/pages/ContactUsPage';
import LoginPage from '@/pages/LoginPage';
import RegisterPage from '@/pages/RegisterPage';
import ForgotPasswordPage from '@/pages/ForgotPasswordPage';
import ResetPasswordPage from '@/pages/ResetPasswordPage';
import DashboardPage from '@/pages/DashboardPage';
import ServicesPage from '@/pages/ServicesPage';
import ServiceDetailsPage from '@/pages/ServiceDetailsPage';
import DocumentUploadPage from '@/pages/DocumentUploadPage';
import PaymentPage from '@/pages/PaymentPage';
import TaxLockerPage from '@/pages/TaxLockerPage';
import ProfilePage from '@/pages/ProfilePage';
import UpdateProfilePage from '@/pages/UpdateProfilePage';
import ChangePasswordPage from '@/pages/ChangePasswordPage';
import AdminDashboardPage from '@/pages/AdminDashboardPage';
import AdminLoginPage from '@/pages/AdminLoginPage';
import AdminRegistrationPage from '@/pages/AdminRegistrationPage';
import ManagerDashboardPage from '@/pages/ManagerDashboardPage';
import EmailVerificationPage from '@/pages/EmailVerificationPage';
import CookieConsent from '@/components/CookieConsent';
import '@/App.css';

const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return user ? children : <Navigate to="/login" replace />;
};

const AdminRoute = ({ children }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  if (user.role !== 'admin') {
    return <Navigate to="/dashboard" replace />;
  }

  return children;
};

const ManagerRoute = ({ children }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  if (user.role !== 'manager') {
    return <Navigate to="/dashboard" replace />;
  }

  return children;
};

function AppRoutes() {
  return (
    <Routes>
      <Route path="/" element={<HomePage />} />
      <Route path="/about" element={<AboutUsPage />} />
      <Route path="/itr-services" element={<ITRServicesPage />} />
      <Route path="/gst-services" element={<GSTServicesPage />} />
      <Route path="/contact" element={<ContactUsPage />} />
      <Route path="/login" element={<LoginPage />} />
      <Route path="/register" element={<RegisterPage />} />
      <Route path="/verify-email" element={<EmailVerificationPage />} />
      <Route path="/forgot-password" element={<ForgotPasswordPage />} />
      <Route path="/reset-password" element={<ResetPasswordPage />} />
      <Route path="/admin-login" element={<AdminLoginPage />} />
      <Route path="/admin-register" element={<AdminRegistrationPage />} />
      
      <Route path="/dashboard" element={
        <ProtectedRoute>
          <DashboardPage />
        </ProtectedRoute>
      } />
      
      <Route path="/services" element={
        <ProtectedRoute>
          <ServicesPage />
        </ProtectedRoute>
      } />
      
      <Route path="/services/:serviceId" element={
        <ProtectedRoute>
          <ServiceDetailsPage />
        </ProtectedRoute>
      } />
      
      <Route path="/service-request/:requestId/upload" element={
        <ProtectedRoute>
          <DocumentUploadPage />
        </ProtectedRoute>
      } />
      
      <Route path="/service-request/:requestId/payment" element={
        <ProtectedRoute>
          <PaymentPage />
        </ProtectedRoute>
      } />
      
      <Route path="/tax-locker" element={
        <ProtectedRoute>
          <TaxLockerPage />
        </ProtectedRoute>
      } />
      
      <Route path="/profile" element={
        <ProtectedRoute>
          <ProfilePage />
        </ProtectedRoute>
      } />
      
      <Route path="/update-profile" element={
        <ProtectedRoute>
          <UpdateProfilePage />
        </ProtectedRoute>
      } />
      
      <Route path="/change-password" element={
        <ProtectedRoute>
          <ChangePasswordPage />
        </ProtectedRoute>
      } />
      
      <Route path="/admin" element={
        <AdminRoute>
          <AdminDashboardPage />
        </AdminRoute>
      } />

      <Route path="/manager" element={
        <ManagerRoute>
          <ManagerDashboardPage />
        </ManagerRoute>
      } />
    </Routes>
  );
}

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <AppRoutes />
        <CookieConsent />
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;